import { motion } from "framer-motion";
import { Nut, Sparkles, Coffee } from "lucide-react";
import { useT } from "../i18n";
import { MANIFESTO_IMAGE } from "../data/content";

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

export const Manifesto = () => {
  const t = useT();
  const tiles = [
    { icon: Nut, title: t.ch1_tile_1_t, desc: t.ch1_tile_1_d },
    { icon: Sparkles, title: t.ch1_tile_2_t, desc: t.ch1_tile_2_d },
    { icon: Coffee, title: t.ch1_tile_3_t, desc: t.ch1_tile_3_d },
  ];

  return (
    <section id="manifesto" className="relative bg-cream-50 py-24 sm:py-36 overflow-hidden" data-testid="manifesto-section">
      <span aria-hidden className="text-outline font-serif font-bold absolute top-6 right-0 text-[16vw] leading-none select-none pointer-events-none">
        01
      </span>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10">
        <motion.p {...reveal} className="font-mono text-xs uppercase tracking-[0.35em] text-bergamote-600 mb-5">
          {t.ch1_label}
        </motion.p>
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-7">
            <motion.h2 {...reveal} className="font-serif font-medium tracking-tight text-3xl sm:text-4xl lg:text-5xl text-bergamote-900 max-w-xl" data-testid="manifesto-title">
              {t.ch1_title}
            </motion.h2>
            <motion.p {...reveal} transition={{ ...reveal.transition, delay: 0.1 }} className="mt-8 text-base sm:text-lg leading-relaxed text-bergamote-800 max-w-2xl">
              {t.ch1_body_1}
            </motion.p>
            <motion.p {...reveal} transition={{ ...reveal.transition, delay: 0.2 }} className="mt-5 text-base sm:text-lg leading-relaxed text-bergamote-800/80 max-w-2xl">
              {t.ch1_body_2}
            </motion.p>

            <div className="mt-12 grid sm:grid-cols-3 gap-5">
              {tiles.map(({ icon: Icon, title, desc }, i) => (
                <motion.div
                  key={title}
                  {...reveal}
                  transition={{ ...reveal.transition, delay: 0.15 + i * 0.12 }}
                  className="group bg-bergamote-50 border border-bergamote-600/15 rounded-2xl p-6 hover:border-saffron-500/50 hover:-translate-y-1 transition-[border-color,transform] duration-300"
                  data-testid={`manifesto-tile-${i}`}
                >
                  <Icon className="w-6 h-6 text-saffron-600 mb-4 group-hover:scale-110 transition-transform duration-300" />
                  <h3 className="font-serif text-xl font-semibold text-bergamote-900">{title}</h3>
                  <p className="mt-2 text-sm text-bergamote-800/80 leading-relaxed">{desc}</p>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.div
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.2 }}
            className="lg:col-span-5 lg:mt-20"
          >
            <div className="rounded-[2.5rem] overflow-hidden border border-bergamote-600/15 shadow-xl shadow-bergamote-600/10 rotate-2 hover:rotate-0 transition-transform duration-700">
              <img
                src={MANIFESTO_IMAGE}
                alt="Dallah coffee pot with sweets on the terrace at La Bergamote"
                className="w-full h-[420px] object-cover hover:scale-105 transition-transform duration-700"
                data-testid="manifesto-image"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
